/**
 * <EpicTool/> — CLIO EPIC mobile view.
 *
 * Displays: today's episode card (number, title, date), play button (if audio_path
 * exists) using expo-av Audio.Sound, else "Generate audio" (POST /api/epic/audio)
 * with expo-speech Speech.speak fallback, transcript, unresolved-question callout,
 * skin chips row (POST /api/epic/skin), Regenerate (POST /api/epic/generate {force:true}),
 * and archive list (GET /api/epic/generate).
 *
 * Frosted-glass aesthetic matching mobile primitives (apps/mobile/src/olmps/primitives.tsx).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { Audio } from 'expo-av';
import { apiFetch } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { Atmos, Chip, GlassCard, Kicker } from '../v10/ui';
import type { ToolProps } from '../olmps/ToolHost';

// expo-speech is not currently in package.json; if added, uncomment:
// import * as Speech from 'expo-speech';

// Types matching web src/interfaces/epic.ts

interface EpisodeArchiveItem {
  id: string;
  date: string;
  title: string;
  episodeNumber: number;
  audioPath?: string | null;
}

type EpicSkinId =
  | 'chronicle'
  | 'parallel_broadcast'
  | 'intel_briefing'
  | 'dojo_report'
  | 'ships_log'
  | 'mythic_realm'
  | 'cinematic_noir'
  | 'resonance_sweep'
  | 'tribunal'
  | 'rotating_anchor';

interface EpicEpisode {
  id: string;
  userId: string;
  date: string;
  episodeNumber: number;
  title: string;
  skin: EpicSkinId;
  script: string;
  editedScript?: string | null;
  unresolvedQuestion: string;
  audioPath?: string | null;
  audioStatus: 'none' | 'pending' | 'ready' | 'error' | 'unconfigured';
  status: 'generating' | 'ready' | 'error';
  createdAt: string;
  updatedAt: string;
}

interface EpicGenerateResponse {
  episode: EpicEpisode | null;
  archive: EpisodeArchiveItem[];
}

interface EpicAudioResponse {
  url: string;
}

interface EpicSkinResponse {
  episode: EpicEpisode | null;
}

// Helper to format date (yyyy-MM-dd to "Month D, YYYY")
function formatDate(dateStr: string): string {
  try {
    const d = new Date(`${dateStr}T00:00:00Z`);
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  } catch {
    return dateStr;
  }
}

// Skin metadata (minimal for display)
const SKIN_NAMES: Record<EpicSkinId, string> = {
  chronicle: 'Chronicle',
  parallel_broadcast: 'Broadcast',
  intel_briefing: 'Briefing',
  dojo_report: 'Dojo Report',
  ships_log: "Ship's Log",
  mythic_realm: 'Mythic',
  cinematic_noir: 'Noir',
  resonance_sweep: 'Resonance',
  tribunal: 'Tribunal',
  rotating_anchor: 'Anchor',
};

const ALL_SKIN_IDS: EpicSkinId[] = [
  'chronicle',
  'parallel_broadcast',
  'intel_briefing',
  'dojo_report',
  'ships_log',
  'mythic_realm',
  'cinematic_noir',
  'resonance_sweep',
  'tribunal',
  'rotating_anchor',
];

export function EpicTool(_props: ToolProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [episode, setEpisode] = useState<EpicEpisode | null>(null);
  const [archive, setArchive] = useState<EpisodeArchiveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [audioGenerating, setAudioGenerating] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const soundRef = useRef<Audio.Sound | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [skinRerendering, setSkinRerendering] = useState<string | null>(null);

  const [expandedArchiveId, setExpandedArchiveId] = useState<string | null>(null);

  // Load today's episode + archive on mount
  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadEpisode = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get today's date in local time (yyyy-MM-dd)
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        // Fetch today's episode
        const data = await apiFetch<EpicGenerateResponse>(`/api/epic/generate?date=${todayStr}`);
        if (cancelled) return;

        setEpisode(data.episode || null);
        setArchive(data.archive || []);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? 'Failed to load episode');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEpisode();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Cleanup sound on unmount
  useEffect(() => {
    return () => {
      if (soundRef.current) {
        soundRef.current.unloadAsync().catch(() => {});
      }
    };
  }, []);

  const handlePlayAudio = useCallback(async () => {
    if (!episode?.audioPath) return;

    try {
      if (soundRef.current) {
        const status = await soundRef.current.getStatusAsync();
        if (status.isLoaded && status.isPlaying) {
          await soundRef.current.pauseAsync();
          setAudioPlaying(false);
          return;
        }
      }

      setAudioPlaying(true);
      const sound = new Audio.Sound();
      soundRef.current = sound;

      await sound.loadAsync({ uri: episode.audioPath });
      await sound.playAsync();

      // Set up completion handler
      sound.setOnPlaybackStatusUpdate((status) => {
        if (status.isLoaded && status.didJustFinish) {
          setAudioPlaying(false);
        }
      });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to play audio');
      setAudioPlaying(false);
    }
  }, [episode?.audioPath]);

  const handleGenerateAudio = useCallback(async () => {
    if (!episode) return;

    try {
      setAudioGenerating(true);
      setError(null);

      const data = await apiFetch<EpicAudioResponse>('/api/epic/audio', {
        method: 'POST',
        body: JSON.stringify({ episodeId: episode.id }),
      });

      if (data.url) {
        setEpisode((ep) =>
          ep ? { ...ep, audioPath: data.url, audioStatus: 'ready' } : null
        );
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to generate audio');
    } finally {
      setAudioGenerating(false);
    }
  }, [episode]);

  const handleSpeechSynthesis = useCallback(() => {
    if (!episode) return;
    const text = episode.editedScript || episode.script;
    if (!text) return;

    // expo-speech support: if added to package.json, uncomment below
    // Speech.stop();
    // Speech.speak(text, {
    //   rate: 1,
    //   pitch: 1,
    //   language: 'en-US',
    // }).catch((e: any) => {
    //   setError(e?.message ?? 'Speech synthesis failed');
    // });

    // Fallback: show message that speech synthesis requires app update
    setError('Speech synthesis requires app update — please generate audio or use the web version.');
  }, [episode]);

  const handleRegenerateEpisode = useCallback(async () => {
    if (!episode) return;

    try {
      setRegenerating(true);
      setError(null);

      const data = await apiFetch<EpicGenerateResponse>('/api/epic/generate', {
        method: 'POST',
        body: JSON.stringify({
          date: episode.date,
          force: true,
        }),
      });

      setEpisode(data.episode || null);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to regenerate episode');
    } finally {
      setRegenerating(false);
    }
  }, [episode]);

  const handleReskinEpisode = useCallback(
    async (skinId: EpicSkinId) => {
      if (!episode) return;

      try {
        setSkinRerendering(skinId);
        setError(null);

        const data = await apiFetch<EpicSkinResponse>('/api/epic/skin', {
          method: 'POST',
          body: JSON.stringify({
            episodeId: episode.id,
            skin: skinId,
          }),
        });

        setEpisode(data.episode || null);
      } catch (e: any) {
        setError(e?.message ?? 'Failed to re-skin episode');
      } finally {
        setSkinRerendering(null);
      }
    },
    [episode]
  );

  if (loading) {
    return (
      <Atmos style={styles.centerContainer}>
        <ActivityIndicator color={t.gold} size="large" />
        <Text style={styles.loadingText}>Loading episode…</Text>
      </Atmos>
    );
  }

  if (error) {
    return (
      <Atmos style={styles.centerContainer}>
        <Text style={styles.errorTitle}>Couldn't load the episode.</Text>
        <Text style={styles.errorDetail}>{error}</Text>
      </Atmos>
    );
  }

  if (!episode) {
    return (
      <Atmos style={styles.centerContainer}>
        <Text style={styles.noEpisodeText}>No episode available for today.</Text>
      </Atmos>
    );
  }

  const transcript = episode.editedScript || episode.script;

  return (
    <Atmos>
    <ScrollView style={styles.root} contentContainerStyle={styles.content}>
      {/* Episode Card */}
      <GlassCard>
        <View style={styles.cardHeader}>
          <View>
            <Kicker>Episode {episode.episodeNumber}</Kicker>
            <Text style={styles.episodeTitle}>{episode.title}</Text>
          </View>
        </View>
        <Text style={styles.episodeDate}>{formatDate(episode.date)}</Text>

        {/* Audio Player or Generation */}
        <View style={styles.audioSection}>
          {episode.audioPath ? (
            <Pressable
              style={[styles.playButton, audioPlaying && styles.playButtonActive]}
              onPress={handlePlayAudio}
            >
              <Text style={styles.playButtonText}>
                {audioPlaying ? 'Pause' : 'Play'}
              </Text>
            </Pressable>
          ) : (
            <View style={styles.audioControls}>
              <Pressable
                style={[styles.generateButton, audioGenerating && styles.generateButtonDisabled]}
                onPress={handleGenerateAudio}
                disabled={audioGenerating}
              >
                <Text style={styles.generateButtonText}>
                  {audioGenerating ? 'Generating…' : 'Generate audio'}
                </Text>
              </Pressable>
              <Pressable style={styles.synthesisButton} onPress={handleSpeechSynthesis}>
                <Text style={styles.synthesisButtonText}>Speak</Text>
              </Pressable>
            </View>
          )}
        </View>
      </GlassCard>

      {/* Transcript */}
      {transcript && (
        <GlassCard>
          <Text style={styles.sectionTitle}>Transcript</Text>
          <Text style={styles.transcriptText}>{transcript}</Text>
        </GlassCard>
      )}

      {/* Unresolved Question */}
      {episode.unresolvedQuestion && (
        <GlassCard style={styles.unresolvedCard}>
          <Text style={styles.unresolvedLabel}>Unresolved</Text>
          <Text style={styles.unresolvedText}>{episode.unresolvedQuestion}</Text>
        </GlassCard>
      )}

      {/* Skin Chips */}
      <GlassCard>
        <Text style={styles.sectionTitle}>Skin</Text>
        <View style={styles.skinChips}>
          {ALL_SKIN_IDS.map((skinId) => {
            const isActive = episode.skin === skinId;
            const isRerendering = skinRerendering === skinId;

            return (
              <Chip
                key={skinId}
                small
                label={SKIN_NAMES[skinId]}
                active={isActive}
                onPress={isRerendering ? undefined : () => handleReskinEpisode(skinId)}
                style={isRerendering ? styles.skinChipDisabled : undefined}
              />
            );
          })}
        </View>
      </GlassCard>

      {/* Actions */}
      <View style={styles.actionRow}>
        <Pressable
          style={[styles.actionButton, regenerating && styles.actionButtonDisabled]}
          onPress={handleRegenerateEpisode}
          disabled={regenerating}
        >
          <Text style={styles.actionButtonText}>
            {regenerating ? 'Regenerating…' : 'Regenerate'}
          </Text>
        </Pressable>
      </View>

      {/* Archive */}
      {archive.length > 0 && (
        <GlassCard>
          <Text style={styles.sectionTitle}>Past Episodes</Text>
          <View style={styles.archiveList}>
            {archive.map((item) => (
              <Pressable
                key={item.id}
                style={styles.archiveItem}
                onPress={() =>
                  setExpandedArchiveId(
                    expandedArchiveId === item.id ? null : item.id
                  )
                }
              >
                <View style={styles.archiveItemHeader}>
                  <View style={styles.archiveItemText}>
                    <Text style={styles.archiveItemTitle}>{item.title}</Text>
                    <Text style={styles.archiveItemMeta}>
                      Episode {item.episodeNumber} · {formatDate(item.date)}
                    </Text>
                  </View>
                  <Text style={styles.archiveItemToggle}>
                    {expandedArchiveId === item.id ? '▼' : '▶'}
                  </Text>
                </View>
              </Pressable>
            ))}
          </View>
        </GlassCard>
      )}
    </ScrollView>
    </Atmos>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    root: { flex: 1 },
    content: { padding: 16, gap: 16, paddingBottom: 32 },

    // Center layouts
    centerContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      gap: 8,
    },
    loadingText: { fontSize: 14, color: t.ivorySoft, marginTop: 12 },
    errorTitle: { fontSize: 16, color: t.lapsed, fontFamily: DISPLAY_FONT, letterSpacing: 2, textTransform: 'uppercase' },
    errorDetail: { fontSize: 13, color: t.ivorySoft, textAlign: 'center' },
    noEpisodeText: { fontSize: 14, color: t.ivorySoft },

    // Episode card
    cardHeader: { marginBottom: 12 },
    episodeTitle: {
      fontSize: 20,
      color: t.ivory,
      marginTop: 4,
      fontFamily: DISPLAY_FONT,
      letterSpacing: 2,
      textTransform: 'uppercase',
    },
    episodeDate: { fontSize: 13, color: t.ivorySoft },

    // Audio section
    audioSection: { marginTop: 12, gap: 8 },
    audioControls: { flexDirection: 'row', gap: 8 },
    playButton: {
      backgroundColor: t.gold,
      boxShadow: `0 6px 18px ${t.goldGlow}`,
      borderRadius: radii.pill,
      paddingVertical: 12,
      paddingHorizontal: 16,
      alignItems: 'center',
    },
    playButtonActive: { opacity: 0.8 },
    playButtonText: { fontWeight: '800', color: t.onGold, fontSize: 14, letterSpacing: 0.4 },
    generateButton: {
      flex: 1,
      backgroundColor: t.gold,
      boxShadow: `0 6px 18px ${t.goldGlow}`,
      borderRadius: radii.pill,
      paddingVertical: 12,
      alignItems: 'center',
    },
    generateButtonDisabled: { opacity: 0.6 },
    generateButtonText: { fontWeight: '800', color: t.onGold, fontSize: 13, letterSpacing: 0.4 },
    synthesisButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: t.gold,
      borderRadius: radii.pill,
      paddingVertical: 12,
      alignItems: 'center',
    },
    synthesisButtonText: { fontWeight: '800', color: t.goldBright, fontSize: 13 },

    // Section title
    sectionTitle: {
      fontSize: 14,
      color: t.ivory,
      marginBottom: 12,
      fontFamily: DISPLAY_FONT,
      letterSpacing: 2.5,
      textTransform: 'uppercase',
    },

    // Transcript
    transcriptText: { fontSize: 13, lineHeight: 20, color: t.ivory },

    // Unresolved
    unresolvedCard: { borderColor: t.behind, padding: 14 },
    unresolvedLabel: {
      fontSize: 10.5,
      fontWeight: '800',
      color: t.behind,
      textTransform: 'uppercase',
      letterSpacing: 2.2,
      marginBottom: 8,
    },
    unresolvedText: { fontSize: 13, lineHeight: 18, color: t.ivory },

    // Skin chips
    skinChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    skinChipDisabled: { opacity: 0.6 },

    // Actions
    actionRow: { gap: 8 },
    actionButton: {
      borderWidth: 1,
      borderColor: t.gold,
      borderRadius: radii.pill,
      paddingVertical: 12,
      alignItems: 'center',
    },
    actionButtonDisabled: { opacity: 0.6 },
    actionButtonText: { fontWeight: '800', color: t.goldBright, fontSize: 13 },

    // Archive
    archiveList: { gap: 8 },
    archiveItem: {
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.lineFaint,
      borderRadius: 14,
      paddingHorizontal: 12,
      paddingVertical: 10,
    },
    archiveItemHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    archiveItemText: { flex: 1 },
    archiveItemTitle: { fontSize: 13, color: t.ivory, fontFamily: DISPLAY_FONT, letterSpacing: 1.5, textTransform: 'uppercase' },
    archiveItemMeta: { fontSize: 11, color: t.ivoryFaint, marginTop: 2 },
    archiveItemToggle: { fontSize: 11, color: t.ivorySoft, marginLeft: 8 },
  }),
);
